/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useContext, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { t } from 'i18next';
import classNames from 'classnames';

// Component imports
import { Button, Tooltip, Typography } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import ChatSession from '../UI/ChatSession/ChatSession';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';
import { ArrowUpIcon } from '../Icons/ArrowUpIcon';

// Misc imports
import { LiveChatContext } from '../../screens/LiveChatScreen';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useLazyFetchChatSessionQuery } from '../../store/liveChat/liveChatApi';
import { LIVECHAT_EVENT_TYPES, LIVECHAT_SCREEN_TABS } from '../../constants/liveChat';
import { CHAT_SESSION_MESSAGE_TYPES } from '../../constants/main';

const LiveChatSession = () => {
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();
  const isRequestsTab = searchParams.get('tab') === LIVECHAT_SCREEN_TABS.NEW || searchParams.get('tab') === LIVECHAT_SCREEN_TABS.REQUESTS_HISTORY;

  const { selectedRequest } = useContext(LiveChatContext);
  const { currentData: activeConversationInfo } = getCurrentQueryState('fetchActiveConversation', { orgId, conversationId: searchParams.get('conversation') });
  const { data: organization } = getCurrentQueryState('fetchOrganization', { orgId });

  const [fetchChatSession] = useLazyFetchChatSessionQuery();
  const [session, setSession] = useState(null);
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);

  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    if (isRequestsTab) {
      setIsHistoryPanelOpen(false);
    }
    if (!selectedRequest || !isRequestsTab) {
      return;
    }
    getSession(selectedRequest);

    return () => setSession(null);
  }, [selectedRequest]);

  useEffect(() => {
    if (!isRequestsTab) {
      setIsHistoryPanelOpen(false);
    }
    if (!activeConversationInfo || isRequestsTab) {
      return;
    }
    getSession(activeConversationInfo);

    return () => setSession(null);
  }, [activeConversationInfo]);

  useEffect(() => {
    if (!isHistoryPanelOpen) {
      return;
    }
    getSession(isRequestsTab ? selectedRequest : activeConversationInfo);
  }, [isHistoryPanelOpen]);

  const getSession = async (item) => {
    const result = await fetchChatSession({ orgId, tenantId: item.tenantId, sessionId: item.platformSessionId });

    if (result?.isSuccess) {
      setSession((result?.data || [])
        .filter(message =>(
          message.liveChatEventType === LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_ACCEPTED
           || message.liveChatEventType === LIVECHAT_EVENT_TYPES.LIVECHAT_TERMINATED
           || message.liveChatEventType === LIVECHAT_EVENT_TYPES.LIVECHAT_REQUEST_CLOSED
           || message.type !== CHAT_SESSION_MESSAGE_TYPES.TRIGGER
        )
          && message.message
        )
        .reverse()
      );
    } else {
      setSession(null);
    }
  };

  return (
    <Fragment>
      {
        !isRequestsTab ? (
          <Tooltip
            destroyTooltipOnHide
            title={t('Chat history is unavailable as session analytics are disabled.')}
            placement={'left'}
            overlayClassName={(!session || !session.length || !activeConversationInfo) ? 'opacity-100' : 'opacity-0'}
          >
            <Button
              onClick={() => setIsHistoryPanelOpen(!isHistoryPanelOpen)}
              size="large"
              shape="circle"
              className="shadow border-0 position-absolute chat-session-history-btn overflow-hidden"
              style={{ top: 12, right: 20, zIndex: 1000 }}
              icon={
                isHistoryPanelOpen ? <ArrowDownIcon style={{ verticalAlign: -4 }} className="fs-lg" /> : <HistoryOutlined style={{ verticalAlign: -2 }} className="fs-lg" />
              }
              disabled={!session || !session.length || !activeConversationInfo}
            >
              <Typography.Text
                hidden={!session || !session.length || !activeConversationInfo}
                className="m-0 text-primary"
                style={{ width: 0, opacity: 0 }}
              >
                {
                  isHistoryPanelOpen ? (
                    <Fragment>
                      <ArrowDownIcon className="mr-2" />
                      {t('Collapse chat history')}
                    </Fragment>
                  ) : (
                    <Fragment>
                      <ArrowUpIcon className="mr-2" />
                      {t('Show chat history')}
                    </Fragment>
                  )
                }
              </Typography.Text>
            </Button>
          </Tooltip>
        ) : null
      }

      {
        isHistoryPanelOpen && session ? (
          <ChatSession
            session={session}
            wrapperClassName={classNames('position-absolute w-100 h-100 bg-layout overflow-y-auto', {
              'border-bottom': !isRequestsTab
            })}
            wrapperStyle={{
              top: 0,
              left: 0,
              zIndex: 999
            }}
            isHistory
            timezone={orgTimezone}
          />
        ) : null
      }
    </Fragment>
  );
};

export default LiveChatSession;
